import type { SchemaSnapshot } from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { ApiError, api } from './api';

export const schemaQuery = { queryKey: ['schema'], queryFn: () => api<SchemaSnapshot>('/schema'), staleTime: 30_000 };

/** The schema registry (types, fields, templates, lists, relation types). */
export const useSchema = () => useQuery(schemaQuery);

/**
 * Admin schema changes: asks for a fresh authenticator code when needed, refreshes the registry after success,
 * and keeps the server's plain-language error for display.
 */
export function useSchemaEdit() {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: ({ path, method = 'POST', body = {} }: { path: string; method?: string; body?: unknown }) =>
      withReauth(() => api<{ id?: string }>(path, { method, body })),
    onSuccess: () => {
      setError(null);
      return qc.invalidateQueries({ queryKey: ['schema'] });
    },
    onError: e => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Try again.');
    },
  });
  return { ...m, error, clearError: () => setError(null) };
}
